/**
 * The address of Vaktplan (issue #74): every page that links into the planner builds it with
 * toVaktplan, and the planner reads it with fromSearch, so the shape is written once.
 *
 * - `?uke=YYYY-MM-DD` opens the week holding that date (TF-AI-01)
 * - `?dag=YYYY-MM-DD` opens the day view on that date (Oversikt's "Åpne dagsplan", 2026-10-09)
 * - `?ny=1&dag=&fra=HH:MM&til=HH:MM[&ansatt=id]` opens the planner's own new-shift editor with that
 *   interval, open or for the person (Oversikt's cover actions); nothing is written until saved
 * - `&ledig=1` beside a week opens the first open shift not yet started, with Hugin's ranked takers
 *   (the welcome dialog's «Fyll en ledig vakt» and Hugin's demo chip, issue #131)
 *
 * The planner reads the address once, at initialisation; its week navigator owns the week after
 * that and does not write to the address, so ordinary navigation and Back are unchanged.
 */
import { mondayOf } from '@timefly/core';

export type VaktplanLink =
  | { day: string; newShift?: { from: string; to: string; userId?: number | null } }
  | { week: string; open?: boolean };

/** An hour-gap that runs to midnight ends at 24:00, which a clock field cannot hold: 23:59. */
const clock = (t: string) => (t === '24:00' ? '23:59' : t);

export function toVaktplan(link: VaktplanLink): string {
  if ('week' in link) return `/vaktplan?uke=${link.week}${link.open ? '&ledig=1' : ''}`;
  const n = link.newShift;
  if (!n) return `/vaktplan?dag=${link.day}`;
  const who = n.userId != null ? `&ansatt=${n.userId}` : '';
  return `/vaktplan?ny=1&dag=${link.day}&fra=${clock(n.from)}&til=${clock(n.to)}${who}`;
}

/**
 * A date checked rather than trusted: it comes from a URL, so anybody can type it. A round trip
 * through Date is what rejects 2026-02-31, which `Date.parse` accepts in some engines and which
 * `mondayOf` would otherwise turn into a week nobody asked for.
 */
export function dayParam(value: string | null): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== value) return null;
  return value;
}
/** ?uke=, the Monday of the week holding a checked date. */
export function weekParam(value: string | null): string | null {
  const day = dayParam(value);
  return day ? mondayOf(day) : null;
}
const clockParam = (v: string | null) => (v && /^([01]\d|2[0-3]):[0-5]\d$/.test(v) ? v : null);

export interface VaktplanSearch {
  /** ?dag=, checked */
  day: string | null;
  /** ?uke=, as the Monday of its week */
  week: string | null;
  /** ?ny=1 with a day and a whole interval, else null */
  newShift: { date: string; from: string; to: string; userId: number | null } | null;
  /** ?ledig=1: open the first open shift ahead */
  openShift: boolean;
}

export function fromSearch(p: URLSearchParams): VaktplanSearch {
  const day = dayParam(p.get('dag'));
  const from = clockParam(p.get('fra'));
  const to = clockParam(p.get('til'));
  const u = Number(p.get('ansatt'));
  return {
    day,
    week: weekParam(p.get('uke')),
    newShift:
      p.get('ny') === '1' && day && from && to
        ? { date: day, from, to, userId: Number.isInteger(u) && u > 0 ? u : null }
        : null,
    openShift: p.get('ledig') === '1',
  };
}
